"use client"

import { useState, useEffect, useMemo } from 'react';
import { createClient } from '@supabase/supabase-js';
import { useAuth, useUser } from '@clerk/nextjs';
import Link from 'next/link';
import { Heart, ThumbsDown, ThumbsUp } from 'lucide-react';
import { ShowIfRole } from '@/components/ShowIfRole';

const supabase = createClient(
    process.env.NEXT_PUBLIC_SUPABASE_URL!,
    process.env.NEXT_PUBLIC_SUPABASE_KEY!
);

const ADMIN_ID = "user_3AtnbF82V4ROWYZ4rH2PlrSsvGk";


function VoteControl({ count, onVote, userVote }: { count: number, onVote: (type: 1 | -1) => void, userVote: number }) {
    return (
        <div className="flex flex-col items-center mr-4 min-w-[32px]">

            <button
                onClick={(e) => { e.preventDefault(); onVote(1); }}
                className={`p-1 rounded hover:bg-stone-100 transition-colors ${userVote === 1 ? 'text-amber-700' : 'text-stone-400'}`}
            >
                <Heart size={20} fill={userVote === 1 ? "currentColor" : "none"} />
            </button>

            <span className={`text-sm font-bold my-0.5 ${count > 0 ? 'text-amber-700' : count < 0 ? 'text-stone-500' : 'text-stone-600'}`}>
                {count || 0}
            </span>

        </div>
    );
}

export default function ForumHub() {
    const { userId, getToken } = useAuth();
    const { user } = useUser();
    const [posts, setPosts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        title: '',
        content_text: '',
        body: 'welcome'
    });
    const isAdmin = userId === ADMIN_ID;
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [userData, setUserData] = useState<{
        [key: string]: {
            rank: any; username: string, imageUrl: string, role: string;
        }
    }>({});
    const [userVotes, setUserVotes] = useState<Record<string, number>>({});
    const [communities, setCommunities] = useState<any[]>([]);

    const [communityForm, setCommunityForm] = useState({ id: '', label: '', description: '' });
    const [isCommunityFormVisible, setIsCommunityFormVisible] = useState(false);

    useEffect(() => {
        fetchCommunities();
        fetchPosts();
    }, []);

    async function handleCreateCommunity(e: React.FormEvent) {
        e.preventDefault();
        if (!userId) return alert("Sign in.");


        const slugify = (text: string) =>
            text.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');



        const { error } = await supabase.from('communities').insert([{
            id: communityForm.label.toLowerCase().replace(/\s+/g, '-'),
            label: communityForm.label,
            description: communityForm.description,
            created_by: userId,
        }]);

        if (error) {
            console.error(error);
        } else {
            setCommunityForm({ id: '', label: '', description: '' });
            setIsCommunityFormVisible(false);
            fetchCommunities();
        }
    }

    async function fetchCommunities() {
        const { data, error } = await supabase
            .from('communities')
            .select('*')
            .order('created_at', { ascending: true });

        if (!error && data) {
            setCommunities(data);
            // Optionally set the default value of body in formData to the first community
            if (data.length > 0) {
                setFormData(prev => ({ ...prev, body: data[0].id }));
            }
        }
    }

    useEffect(() => {
        fetchPosts();
    }, []);

    useEffect(() => {
        if (userId) fetchUserVotes();
    }, [userId]);

    async function fetchPosts() {
        // Fetch forum posts and journals, merge and sort by date
        const [postsRes, journalsRes] = await Promise.all([
            supabase.from('forum_posts').select('*'),
            supabase.from('journals').select('*')
        ]);

        const forumPosts = postsRes.data || [];
        const journals = journalsRes.data || [];

        // Map journals to the same UI shape, mark them as journals
        const mappedJournals = journals.map((j: any) => ({
            id: `journal_${j.id}`,
            _orig_id: j.id,
            title: j.title,
            slug: j.slug,
            content_text: j.text || j.description || j.content_text || '',
            body: j.body || j.community || '',
            user_id: j.user_id,
            created_at: j.created_at,
            authority: j.authority || 'standard',
            subcommunity: j.subcommunity || '',
            is_settled: false,
            isJournal: true
        }));

        const combined = [...forumPosts, ...mappedJournals].sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        if (postsRes.error || journalsRes.error) {
            console.error(postsRes.error || journalsRes.error);
        }

        setPosts(combined);

        // Fetch user info for all unique user ids
        const uniqueUserIds = Array.from(new Set(combined.map((item: any) => item.user_id)));
        uniqueUserIds.forEach(async (id) => {
            if (!id) return;
            try {
                const response = await fetch(`/api/clerk-user?userId=${id}`);
                const info = await response.json();
                setUserData(prev => ({
                    ...prev,
                    [id]: { username: info.username, imageUrl: info.imageUrl, rank: info.rank, role: info.role }
                }));
            } catch (err) {
                console.error(err);
            }
        });

        setLoading(false);
    }

    async function fetchUserVotes() {
        const { data, error } = await supabase
            .from('post_votes')
            .select('post_id, vote_type')
            .eq('user_id', userId);

        if (!error && data) {
            const votesMap: Record<string, number> = {};
            data.forEach(v => votesMap[v.post_id] = v.vote_type);
            setUserVotes(votesMap);
        }
    }

    async function handleVote(postId: string, direction: 1 | -1, postSettled: boolean) {
        if (!userId) return alert("Please sign in to vote");
        if (postSettled) return alert("Post is settled; voting is no longer allowed");

        const { data: newCount, error } = await supabase.rpc('handle_vote', {
            target_table: 'forum_posts',
            tracker_table: 'post_votes',
            row_id: postId,
            user_id_val: userId,
            new_vote_type: direction
        });

        if (!error) {
            setPosts(prev => prev.map(p => p.id === postId ? { ...p, votes: newCount } : p));
            setUserVotes(prev => {
                const next = { ...prev };
                if (prev[postId] === direction) delete next[postId];
                else next[postId] = direction;
                return next;
            });
        }
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!userId) return alert("Please sign in");

        const slug = formData.title
            .toLowerCase()
            .replace(/[^\w ]+/g, '')
            .replace(/ +/g, '-');

        const { error } = await supabase.from('forum_posts').insert([
            {
                title: formData.title,
                content_text: formData.content_text,
                body: formData.body,
                slug,
                user_id: userId,
                content_type: 'text',
                is_settled: false,
            },
        ]);

        if (error) {
            console.error(error);
        } else {
            setFormData({ title: '', content_text: '', body: 'welcome' });
            setIsFormVisible(false);
            fetchPosts();
        }
    }

    const capitalize = (s?: string) => s ? s.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';


    const groupedPosts = useMemo(() => {
        const groups: Record<string, any[]> = {};

        communities.forEach(c => {
            groups[c.id] = [];
        });

        posts.forEach(post => {
            const communityId = post.body?.toLowerCase();
            if (communityId && groups[communityId] !== undefined) {
                groups[communityId].push(post);
            }
        });

        const sliced: Record<string, any[]> = {};
        communities.forEach(c => {
            sliced[c.id] = groups[c.id].slice(0, 3);
        });

        return sliced;
    }, [posts, communities]);

    return (
        <main className="min-h-screen bg-stone-50 text-stone-900   font-serif">
            <div className='bg-whitfe'>
                <div className="bg-white pt-12 border-b border-amber-800">
                    <header className="text-center bg-white mb-0 max-w-3xl mx-auto border- border-amber-800">
                        <h1 className="text-4xl font-bold tracking-wide text-amber-800 uppercase">
                            Holy American Church
                        </h1>
                        <p className="mt-3 text-stone-600 text-lg">
                            The home of the Holy American Catholic Church.
                        </p>
                        <div className='flex mx-auto justify-center'>
                            <img src='/crucifixj.jpg' className={`mt-5 ${false && "-translate-x-25"}`} width={100} />
                        </div>
                        <div className="mt-6 w-full border-tf border-amber-800 mx-auto" />



                    </header>
                </div>
                <div className='max-w-3xl mx-auto pb-12 mt-12'>

                    <div className="flex justify-center -mt-0 mb-8">
                        {userId && !isCommunityFormVisible && isAdmin && (


                            <button
                                onClick={() => setIsCommunityFormVisible(true)}
                                className="bg-amber-800 hover:bg-amber-950 text-stone-100 px-6 py-2.5 rounded font-sans tracking-wide uppercase text-xs transition-colors"
                            >
                                Create Society
                            </button>



                        )}
                            <Link
                                href="/church/communion"
                                className="bg-cyan-800 ml-3 hover:bg-cyan-950 text-stone-100 px-6 py-2.5 rounded font-sans tracking-wide uppercase text-xs transition-colors"
                            >
                                Communion
                            </Link>
                          <Link
                                href="/church/penance"
                                className="bg-blue-800 ml-3 hover:bg-blue-950 text-stone-100 px-6 py-2.5 rounded font-sans tracking-wide uppercase text-xs transition-colors"
                            >
                                Penance
                            </Link>




                    </div>    {userId && isCommunityFormVisible && (
                        <form onSubmit={handleCreateCommunity} className="mb-12 text-stone-900 p-6 border border-stone-200 rounded-lg bg-white shadow-sm font-sans">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-lg font-bold uppercase tracking-wider text-amber-900">Create New Society</h2>
                                <button
                                    type="button"
                                    onClick={() => setIsCommunityFormVisible(false)}
                                    className="text-stone-400 hover:text-stone-900 text-sm"
                                >
                                    Cancel
                                </button>
                            </div>



                            <label className="block text-xs font-bold uppercase text-stone-500 mb-1">Label</label>
                            <input
                                className="w-full p-2 mb-4 border border-stone-200 rounded focus:border-amber-800 outline-none"
                                placeholder="Label"
                                value={communityForm.label}
                                onChange={(e) => setCommunityForm({ ...communityForm, label: e.target.value })}
                                required
                            />

                            <label className="block text-xs font-bold uppercase text-stone-500 mb-1">Description</label>
                            <textarea
                                className="w-full p-2 mb-4 border border-stone-200 rounded focus:border-amber-800 outline-none"
                                placeholder="Description..."
                                rows={3}
                                value={communityForm.description}
                                onChange={(e) => setCommunityForm({ ...communityForm, description: e.target.value })}
                            />

                            <button type="submit" className="bg-amber-800 hover:bg-amber-950 text-stone-100 px-5 py-2 rounded text-sm font-medium tracking-wide uppercase transition-colors">
                                Create Society
                            </button>
                        </form>
                    )}

                    {userId && !isFormVisible && false && (
                        <div className="flex justify-center mb-12">
                            <button
                                onClick={() => setIsFormVisible(true)}
                                className="bg-amber-800 hover:bg-amber-950 text-stone-100 px-6 py-2.5 rounded font-sans tracking-wide uppercase text-xs transition-colors"
                            >
                                Create a Post
                            </button>
                        </div>
                    )}

                    {userId && isFormVisible && (
                        <form onSubmit={handleSubmit} className="mb-12 text-stone-900 p-6 border border-stone-200 rounded-lg bg-white shadow-sm font-sans">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-lg font-bold uppercase tracking-wider text-amber-900">Create New Post</h2>
                                <button
                                    type="button"
                                    onClick={() => setIsFormVisible(false)}
                                    className="text-stone-400 hover:text-stone-900 text-sm"
                                >
                                    Cancel
                                </button>
                            </div>

                            <label className="block text-xs font-bold uppercase text-stone-500 mb-1">Sub-Forum Community</label>
                            <select
                                className="w-full p-2 mb-4 border border-stone-200 rounded bg-white outline-none cursor-pointer focus:border-amber-800"
                                value={formData.body}
                                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                required
                            >
                                {communities.map((comm) => (
                                    <option key={comm.id} value={comm.id}>
                                        {comm.label}
                                    </option>
                                ))}
                            </select>

                            <label className="block text-xs font-bold uppercase text-stone-500 mb-1">Title</label>
                            <input
                                className="w-full p-2 mb-4 border border-stone-200 rounded focus:border-amber-800 outline-none"
                                placeholder="Post Title"
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                required
                            />

                            <label className="block text-xs font-bold uppercase text-stone-500 mb-1">Content</label>
                            <textarea
                                className="w-full p-2 mb-4 border border-stone-200 rounded focus:border-amber-800 outline-none"
                                placeholder="Type content here..."
                                rows={4}
                                value={formData.content_text}
                                onChange={(e) => setFormData({ ...formData, content_text: e.target.value })}
                                required
                            />

                            <button type="submit" className="bg-amber-800 hover:bg-amber-950 text-stone-100 px-5 py-2 rounded text-sm font-medium tracking-wide uppercase transition-colors">
                                Submit Post
                            </button>
                        </form>
                    )}

                    <section className="space-y-14">
                        {communities.map((forum) => (
                            <div key={forum.id}>
                                <Link
                                    href={`/church/society/${forum.id}`}
                                    className="text-2xl font-bold text-amber-800 hover:text-amber-950 transition-colors uppercase tracking-widest"
                                >
                                    {forum.label}
                                </Link>
                                <div className="mt-4 space-y-4 border-l border-stone-200 pl-5">
                                    {loading ? (
                                        <p className="text-stone-400 text-sm font-sans italic">Loading posts...</p>
                                    ) : groupedPosts[forum.id]?.length > 0 ? (
                                        groupedPosts[forum.id].map((post) => (

                                            <div
                                                key={post.id}
                                                className={`flex border border-stone-200 ${post.authority === "dogma" && "border-l-5 border-l-red-600 border-y"} items-start bg-white p-4 rounded-lg shadow-sm hover:border-amber-700/50 transition-colors`}
                                            >
                                                {!post.isJournal && (
                                                    <VoteControl
                                                        count={post.votes}
                                                        userVote={userVotes[post.id] || 0}
                                                        onVote={(dir) => handleVote(post.id, dir, post.is_settled)}
                                                    />
                                                )}

                                                <div className="flex-1 min-w-0">
                                                    <Link href={post.isJournal ? `/church/journals/${post.slug}` : `/church/${post.slug}`} className="block">
                                                        <h4 className="text-base font-semibold text-stone-900 hover:text-amber-800 transition-colors">
                                                            {post.title}
                                                        </h4>
                                                        {post.subcommunity && (
                                                            <span className="bg-stone-200 px-1.5 py-0.5 rounded text-[10px] uppercase font-bold text-stone-600 mr-2">
                                                                {post.subcommunity}
                                                            </span>
                                                        )}
                                                        <p className="text-sm text-stone-500 mt-1 leading-relaxed line-clamp-2 font-sans">
                                                            {post.content_text}
                                                        </p>
                                                    </Link>

                                                    <div className="mt-3 text-xs text-stone-400 font-sans flex items-center justify-between">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span>Posted on {new Date(post.created_at).toLocaleDateString()} by</span>
                                                            {userData[post.user_id] ? (
                                                                <Link
                                                                    href={`/profile/${userData[post.user_id].username}`}
                                                                    className="flex items-center gap-1 text-amber-800 font-medium"
                                                                >
                                                                    <img
                                                                        src={userData[post.user_id].imageUrl}
                                                                        alt=""
                                                                        className="w-4 h-4 rounded-full object-cover border border-stone-100"
                                                                    />

                                                                    {userData[post.user_id]?.rank && (
                                                                        <span className='bg-purple-300 px-1 text-stone-900 rounded-md'>
                                                                            {userData[post.user_id]?.rank === 'Founding Father' ? "Founding Father" : userData[post.user_id]?.rank}
                                                                        </span>
                                                                    )}
                                                                    {userData[post.user_id]?.role === `Bishop of ${capitalize(post.body)}` && (
                                                                        <span className='bg-purple-300 px-1 text-stone-900 rounded-md'>
                                                                        </span>
                                                                    )}
                                                                    <span className="hover:underline">{userData[post.user_id].username}</span>
                                                                </Link>
                                                            ) : (
                                                                <span className="flex items-center gap-1">
                                                                    <span className="w-4 h-4 rounded-full bg-stone-200 animate-pulse inline-block" />
                                                                    {post.user_id === userId ? 'You' : 'Loading...'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-stone-400 text-sm font-sans italic">No posts in this society yet.</p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </section>
                </div>
            </div>
        </main>
    );
}
